import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { map, OperatorFunction } from 'rxjs';
import { Option } from '@sapphire/result';

import {
  AdvancedFeasibilityFilters,
  RESPONDENT_LIST_ORIGIN,
  RespondentListUploadState,
  SurveyLaunchFormState,
  SurveyLocationUploadState,
  SurveyMarginFormState,
  SurveyPricingSnapshot,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import { SurveyAudienceForm, SurveyLaunchSubmissionForm } from '../../../utils';
import {
  selectAdvancedFeasibilityFilters,
  selectAdvancedFeasibilityFiltersFor,
  selectAudienceChildren,
  selectLocationFileUpload,
  selectLocationFileUploadFor,
  selectMarginForm,
  selectMarginFormFor,
  selectSuppliersMetaData,
  selectSuppliersMetaDataFor,
  selectSurveyFormState,
  selectSurveyFormStateFor,
} from '../../audience/survey-creation-audience.selectors';
import {
  selectSurveyFor,
  selectSurveyId,
} from '../survey-creation-core.selectors';
import {
  selectFeasibilityCpi,
  selectFeasibilityCpiFor,
  selectPricing,
  selectPricingFor,
} from '../../insights/survey-creation-insights.selectors';
import {
  selectLaunchForm,
  selectLaunchFormFor,
  selectRespondentListUpload,
  selectRespondentListUploadFor,
} from '../../launch/survey-creation-launch.selectors';
import { SurveyChildKey } from '../../survey-creation-shared-state.model';
import { SurveyCreationChildAudienceState } from '../../survey-creation-state.model';
import { SurveyCreationPricingState } from '../../insights/survey-creation-insights-state.model';
import { SuppliersMetaData } from '../../audience/survey-creation-suppliers-meta-data-state.model';

/**
 * Snapshot of the form + pricing state a create or a save gate starts from —
 * surveyId may be null. Shared by `SurveyAudienceCreateEffects` (audience,
 * create-only) and `SurveyCreationSaveEffects` (core, the multi-stage save
 * family): both stages read the exact same selectors before running
 * their own gate, so the read lives in one place instead of two.
 */
export interface SurveyFormContext {
  /** The save-* action that started this run; its props decide how it reports. */
  readonly trigger: Action;
  readonly surveyId: Nullable<number>;
  readonly audienceForm: SurveyAudienceForm;
  readonly launchForm: SurveyLaunchSubmissionForm;
  /** Only ever filled behind the `enableTargetMargin` service-operator gate. */
  readonly marginForm: SurveyMarginFormState;
  readonly pricing: SurveyPricingSnapshot;
  readonly advancedFeasibilityFilters: AdvancedFeasibilityFilters;
  /** PSIDs from the buyer's own Include/Exclude upload; empty for any other origin. */
  readonly respondentListPsids: string[];
  /** More than one means a multi-locale pre-draft, never a standard survey. */
  readonly childrenCount: number;
  /** The record's uploaded location file; its path rides along on every save. */
  readonly locationFileUpload: SurveyLocationUploadState;
}

export function gatherSurveyFormContext(
  store: Store,
): OperatorFunction<Action, SurveyFormContext> {
  return (trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => [
        store.select(selectSurveyId),
        store.select(selectSurveyFormState),
        store.select(selectLaunchForm),
        store.select(selectMarginForm),
        store.select(selectPricing),
        store.select(selectFeasibilityCpi),
        store.select(selectAdvancedFeasibilityFilters),
        store.select(selectAudienceChildren),
        store.select(selectSuppliersMetaData),
        store.select(selectRespondentListUpload),
        store.select(selectLocationFileUpload),
      ]),
      map(
        ([
          trigger,
          surveyId,
          formState,
          launchFormState,
          marginState,
          pricingState,
          feasibilityCpi,
          advancedFeasibilityFilters,
          children,
          suppliersMetaData,
          respondentListUpload,
          locationFileUpload,
        ]) =>
          toSurveyFormContext({
            trigger,
            surveyId,
            formState,
            launchFormState,
            marginState,
            pricingState,
            feasibilityCpi,
            advancedFeasibilityFilters,
            childrenCount: Object.keys(children).length,
            suppliersMetaData,
            respondentListUpload,
            locationFileUpload,
          }),
      ),
    );
}

// The same snapshot for any child, not only the active one.
export function gatherSurveyFormContextFor(
  store: Store,
  key: SurveyChildKey,
): OperatorFunction<Action, SurveyFormContext> {
  return (trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => [
        store.select(selectSurveyFor(key)),
        store.select(selectSurveyFormStateFor(key)),
        store.select(selectLaunchFormFor(key)),
        store.select(selectMarginFormFor(key)),
        store.select(selectPricingFor(key)),
        store.select(selectFeasibilityCpiFor(key)),
        store.select(selectAdvancedFeasibilityFiltersFor(key)),
        store.select(selectAudienceChildren),
        store.select(selectSuppliersMetaDataFor(key)),
        store.select(selectRespondentListUploadFor(key)),
        store.select(selectLocationFileUploadFor(key)),
      ]),
      map(
        ([
          trigger,
          survey,
          formState,
          launchFormState,
          marginState,
          pricingState,
          feasibilityCpi,
          advancedFeasibilityFilters,
          children,
          suppliersMetaData,
          respondentListUpload,
          locationFileUpload,
        ]) =>
          toSurveyFormContext({
            trigger,
            surveyId: Option.from(survey).mapOr(null, ({ id }) => id),
            formState,
            launchFormState,
            marginState,
            pricingState,
            feasibilityCpi,
            advancedFeasibilityFilters,
            childrenCount: Object.keys(children).length,
            suppliersMetaData,
            respondentListUpload,
            locationFileUpload,
          }),
      ),
    );
}

interface SurveyFormContextSource {
  readonly trigger: Action;
  readonly surveyId: Nullable<number>;
  readonly formState: Pick<
    SurveyCreationChildAudienceState,
    'metadata' | 'qualifications' | 'quotas'
  >;
  readonly launchFormState: SurveyLaunchFormState;
  readonly marginState: SurveyMarginFormState;
  readonly pricingState: SurveyCreationPricingState;
  readonly feasibilityCpi: number;
  readonly advancedFeasibilityFilters: AdvancedFeasibilityFilters;
  readonly childrenCount: number;
  readonly suppliersMetaData: SuppliersMetaData;
  readonly respondentListUpload: RespondentListUploadState;
  readonly locationFileUpload: SurveyLocationUploadState;
}

function toSurveyFormContext({
  trigger,
  surveyId,
  formState,
  launchFormState,
  marginState,
  pricingState,
  feasibilityCpi,
  advancedFeasibilityFilters,
  childrenCount,
  suppliersMetaData,
  respondentListUpload,
  locationFileUpload,
}: SurveyFormContextSource): SurveyFormContext {
  return {
    trigger,
    surveyId,
    audienceForm: SurveyAudienceForm.from(formState),
    launchForm: SurveyLaunchSubmissionForm.from(launchFormState),
    marginForm: marginState,
    pricing: {
      ...pricingState,
      feasibilityCpi,
      defaultPurePriceOverride: suppliersMetaData.defaultPurePriceOverride,
      supplierDefaultAllocation: suppliersMetaData.supplierDefaultAllocation,
    } satisfies SurveyPricingSnapshot,
    advancedFeasibilityFilters,
    respondentListPsids:
      respondentListUpload.origin === RESPONDENT_LIST_ORIGIN.BUYER_UPLOAD
        ? respondentListUpload.items
        : [],
    childrenCount,
    locationFileUpload,
  };
}
